import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useFeatureNav } from '../../hooks/useFeatureNav';
import {
  Wand2, ArrowLeft, ArrowRight, Check, CheckCircle2, Loader2, Sparkles, Boxes, FlaskConical,
  Mic, Square, Paperclip,
} from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { transcribeAudio } from '../../services/orbitVoiceApi';
import type { SkillPrefill, PersonalSkill } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import { Card, MonoLabel } from './dashboards/ui';

// SKL-5: talk instead of type. Records, transcribes, and appends to the box.
const DictateButton: React.FC<{ onText: (t: string) => void }> = ({ onText }) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  if (!supported) return null;

  // A second Stop click before onstop fires would call stop() on an inactive
  // recorder, which throws InvalidStateError.
  const stop = () => { try { recRef.current?.stop(); } catch { /* already stopped */ } };
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
          const result = await transcribeAudio(blob);
          const text = typeof result === 'string' ? result : result?.text;
          if (text && text.trim()) onText(text.trim());
        } catch { /* dictation is a convenience — typing still works */ }
        finally { setBusy(false); }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch { /* mic denied — typing still works */ }
  };

  if (busy) {
    return <span className="inline-flex items-center gap-1 text-[11.5px] text-ink-soft"><Loader2 size={12} className="animate-spin" /> transcribing…</span>;
  }
  if (recording) {
    return (
      <button type="button" onClick={stop}
        className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-red-600 hover:text-red-700">
        <Square size={12} /> Stop
      </button>
    );
  }
  return (
    <button type="button" onClick={start}
      className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-ink-dim hover:text-brand-600">
      <Mic size={12} /> Talk instead
    </button>
  );
};

// SKL-5: "send us the folder" — attach example files; text-like contents are
// read in the browser and appended, other types are referenced by name.
const TEXT_EXTENSIONS = /\.(txt|md|csv|json|html?|xml|yml|yaml|log)$/i;
const AttachExamples: React.FC<{ onText: (t: string) => void }> = ({ onText }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handle = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const parts: string[] = [];
    for (const f of Array.from(files).slice(0, 10)) {
      if (TEXT_EXTENSIONS.test(f.name) && f.size <= 200_000) {
        try {
          const text = await f.text();
          parts.push(`--- ${f.name} ---\n${text.trim().slice(0, 8000)}`);
          continue;
        } catch { /* fall through to name-only */ }
      }
      parts.push(`(attached example file: ${f.name})`);
    }
    if (parts.length) onText(parts.join('\n\n'));
    if (inputRef.current) inputRef.current.value = '';
  };
  return (
    <>
      <button type="button" onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-ink-dim hover:text-brand-600">
        <Paperclip size={12} /> Attach files
      </button>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => void handle(e.target.files)} />
    </>
  );
};

// Module 3/4 — the guided skill builder. Two modes, one wizard:
//  · context  (M3): a reusable "about me" block, from your onboarding.
//  · workflow (M4): a repeatable AI workflow for a real job task — then you vet it.
// Every save creates a NEW, separate skill (kept newest-first). "Revise" (via
// ?revise=<id>) seeds the wizard from an existing skill but still saves a fresh
// instance, so the one you revised stays intact alongside the new one.
type Mode = 'context' | 'workflow';
const STEPS: Record<Mode, string[]> = {
  context: ['You', 'Always know', 'Your style', 'Preview'],
  workflow: ['The skill', 'The steps', 'An example', 'Preview'],
};

const assembleContext = (about: string, extras: string, style: string): string => [
  about.trim(),
  extras.trim() && `What AI should always keep in mind:\n${extras.trim()}`,
  style.trim() && `My style preferences:\n${style.trim()}`,
].filter(Boolean).join('\n\n');

// TD-3: a starter skill must not bypass the delegation profile. The send step
// is computed by the backend (the one delegation resolver) into the prefill's
// process_steps; when the profile restricts sending, that step replaces any
// send line the starter carries (or is appended if the starter has none).
const withSendGuard = (steps: string[], pf: SkillPrefill | null): string[] => {
  if (!pf) return steps;
  if (pf.act_policy !== 'no' && pf.act_policy !== 'approval') return steps;
  const guard = (pf.process_steps || []).find((s) => /^send\s*:/i.test(s));
  if (!guard) return steps;
  const idx = steps.findIndex((s) => /^send\s*:/i.test(s.trim()));
  if (idx < 0) return [...steps, guard];
  return steps.map((s, j) => {
    if (j === idx) return guard;
    return s;
  });
};

const panelParams = (route?: string): URLSearchParams | null => {
  if (!route) return null;
  return new URLSearchParams(route.split('?')[1] || '');
};

const SkillBuilderPage: React.FC<{ panelRoute?: string }> = ({ panelRoute }) => {
  // In a panel there is no URL to read the variant and the seed out of, so the
  // route that opened it is passed in instead. On its own page, unchanged.
  const { go, done } = useFeatureNav();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const path = panelRoute?.split('?')[0] || location.pathname;
  const query = panelParams(panelRoute) || searchParams;
  const mode: Mode = path.endsWith('/workflow') ? 'workflow' : 'context';
  const reviseId = query.get('revise');
  // SKL-8: a lesson's starter skill — the builder opens pre-seeded from a real
  // marketplace skill ("customise this"), never a blank box.
  const fromId = query.get('from');
  // A repeated-use suggestion opens the builder already filled in: the person
  // accepted an offer, so making them retype what the offer said would be a
  // strange way to thank them. Only the two fields the offer actually knows.
  const seededName = query.get('name');
  const seededProblem = query.get('problem');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<SkillPrefill | null>(null);
  // The skill we're revising FROM (seed only) — we still save a brand-new instance.
  const [reviseFrom, setReviseFrom] = useState<PersonalSkill | null>(null);

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  // context fields
  const [about, setAbout] = useState('');
  const [extras, setExtras] = useState('');
  const [style, setStyle] = useState('');
  // workflow fields
  const [stepsText, setStepsText] = useState('');
  const [exampleIn, setExampleIn] = useState('');
  const [exampleOut, setExampleOut] = useState('');

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // SKL-4: near matches from the marketplace for the name being typed.
  const [suggestions, setSuggestions] = useState<Array<{ id: string; name: string; problem: string | null; process_steps: string[] }>>([]);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [starterName, setStarterName] = useState<string | null>(null);
  const [starterProblem, setStarterProblem] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pf, mine] = await Promise.all([aaiApi.getSkillPrefill(mode), aaiApi.getMySkills()]);
      setPrefill(pf.data);
      // Revise mode seeds from a specific skill; a plain build seeds from the
      // onboarding draft. Either way, saving creates a new, separate skill.
      const seed = reviseId ? (mine.data.skills || []).find((s) => s.id === reviseId) || null : null;
      setReviseFrom(seed);
      // SKL-8: a starter skill from a lesson outranks the generic prefill.
      let starter: { name: string; problem: string | null; process_steps: string[]; example_input?: string | null; example_output?: string | null } | null = null;
      if (fromId && !seed) {
        try { starter = (await aaiApi.getSkill(fromId)).data as any; } catch { /* fall back to prefill */ }
      }
      if (starter) {
        setStarterName(starter.name);
        setStarterProblem(starter.problem);
      }
      setName(seed ? `${seed.name} (revised)` : starter ? `${starter.name} (my version)` : (seededName || pf.data.name));
      if (mode === 'context') {
        setAbout((seed?.example_input ?? starter?.example_input ?? pf.data.example_input) || '');
      } else {
        let steps = (seed?.process_steps ?? starter?.process_steps ?? pf.data.process_steps) || [];
        if (starter && !seed) steps = withSendGuard(steps, pf.data);
        setStepsText(steps.join('\n'));
        setExampleIn(seed?.example_input || starter?.example_input || '');
        setExampleOut(seed?.example_output || starter?.example_output || '');
      }
      setError(null);
    } catch {
      setError('Could not open the skill builder.');
    } finally {
      setLoading(false);
    }
  }, [mode, reviseId, fromId]);
  useEffect(() => { void load(); }, [load]);

  // SKL-4: as the name takes shape (workflow mode, fresh build), look for close
  // matches in the marketplace — "twenty people built something like this".
  useEffect(() => {
    if (mode !== 'workflow' || reviseId || fromId || suggestionsDismissed) return;
    const q = name.trim();
    if (q.length < 4) { setSuggestions([]); return; }
    // Stale-response guard: the timer cleanup can't cancel a request already
    // in flight, so an earlier slow response could overwrite a newer search.
    let stale = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await aaiApi.suggestSkills(q);
        if (!stale) setSuggestions(data.matches || []);
      } catch {
        if (!stale) setSuggestions([]);
      }
    }, 450);
    return () => { stale = true; clearTimeout(t); };
  }, [name, mode, reviseId, fromId, suggestionsDismissed]);

  const adoptSuggestion = (s: { name: string; problem: string | null; process_steps: string[] }) => {
    setStepsText(withSendGuard(s.process_steps || [], prefill).join('\n'));
    setStarterName(s.name);
    setStarterProblem(s.problem);
    setSuggestions([]);
    setSuggestionsDismissed(true);
  };

  const save = async () => {
    if (!prefill) return;
    setSaving(true);
    setError(null);
    const body = mode === 'context'
      ? {
          name: name.trim() || prefill.name,
          problem: seededProblem || prefill.problem,
          process_steps: prefill.process_steps,
          example_input: assembleContext(about, extras, style),
        }
      : {
          name: name.trim() || prefill.name,
          problem: starterProblem || seededProblem || prefill.problem,
          process_steps: stepsText.split('\n').map((s) => s.trim()).filter(Boolean),
          example_input: exampleIn,
          example_output: exampleOut,
        };
    try {
      // Always create a new, separate skill — revising never overwrites the original.
      await aaiApi.createMySkill({ ...body, kind: mode });
      setSaved(true);
    } catch {
      setError('Could not save your skill. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-2xl mx-auto"><Loading label="Opening the skill builder…" /></div>;
  if (error && !prefill) return <div className="max-w-2xl mx-auto"><ErrorNote message={error} /></div>;

  const title = mode === 'context' ? 'Build your context-about-you skill' : 'Build a real workflow skill';
  const subtitle = mode === 'context'
    ? 'A reusable block about you and your work — paste it before any AI task so answers fit your role.'
    : 'Turn one recurring job task into a repeatable AI workflow — then prove it works by running it.';

  const Header = () => (
    <>
      <div className="flex items-center gap-2 mb-1">
        <Wand2 size={22} className="text-brand-500" />
        <h1 className="text-2xl font-semibold text-ink">{title}</h1>
      </div>
      <p className="text-sm text-ink-dim mb-5">{subtitle}</p>
    </>
  );

  // ── Saved ────────────────────────────────────────────────────────────────
  if (saved) {
    return (
      <div className="max-w-2xl mx-auto">
        <Header />
        <Card className="text-center py-9">
          <span className="mx-auto mb-3 grid place-items-center h-14 w-14 rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 size={28} />
          </span>
          <p className="text-[17px] font-semibold text-ink">
            {mode === 'context' ? 'Your first skill is built 🎉' : 'Your workflow skill is saved'}
          </p>
          <p className="text-[13px] text-ink-dim mt-1 max-w-sm mx-auto">
            {mode === 'context'
              ? "It's saved to your tools. Reuse it before any AI task — and update it as your work changes."
              : "Now the real work: run it on real tasks, note what happened, and refine it. Doing it once doesn't count."}
          </p>
          <div className="flex items-center justify-center gap-3 mt-5">
            {/* H1 made My Tools a tabbed hub, so name the tab rather than
                relying on its default. `skills` is the adoption page, which is
                where the vetting section lives; an employee lands on that page
                as their FIRST tab, so for them the hub falls back to it and the
                destination is the same either way. */}
            <button onClick={() => go(mode === 'workflow' ? '/my-tools?tab=skills' : '/my-tools?tab=tools')}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
              {mode === 'workflow' ? <><FlaskConical size={15} /> Go vet it</> : <><Boxes size={15} /> See it in My Tools</>}
            </button>
            <button onClick={() => done('/my-training')}
              className="inline-flex items-center gap-1.5 rounded-full bg-canvas-inset hover:bg-line text-ink text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
              Back to training
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // ── No onboarding yet ──────────────────────────────────────────────────────
  if (prefill && !prefill.has_brief && !reviseFrom) {
    return (
      <div className="max-w-2xl mx-auto">
        <Header />
        <Card className="text-center py-9">
          <span className="mx-auto mb-3 grid place-items-center h-14 w-14 rounded-full bg-canvas-inset text-ink-soft">
            <Sparkles size={24} />
          </span>
          <p className="text-[15px] font-semibold text-ink mb-1">Let's get to know you first</p>
          <p className="text-[13px] text-ink-dim max-w-sm mx-auto mb-5">
            We draft this from your onboarding profile. Complete the quick readiness conversation and we'll set it up for you.
          </p>
          <button onClick={() => go('/onboarding')}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
            <Sparkles size={15} /> Complete onboarding
          </button>
        </Card>
      </div>
    );
  }

  const stepValid = step !== 0 || !!name.trim();
  const steps = STEPS[mode];

  return (
    <div className="max-w-2xl mx-auto">
      <Header />

      {/* Stepper */}
      <div className="flex items-center gap-1.5 mb-5">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1.5 ${i === step ? 'text-brand-700' : i < step ? 'text-emerald-600' : 'text-ink-soft'}`}>
              <span className={`grid place-items-center h-6 w-6 rounded-full text-[11px] font-bold ${i === step ? 'bg-brand-100 text-brand-700' : i < step ? 'bg-emerald-50 text-emerald-600' : 'bg-canvas-inset text-ink-soft'}`}>
                {i < step ? <Check size={13} /> : i + 1}
              </span>
              <span className="text-[12px] font-semibold hidden sm:inline">{s}</span>
            </div>
            {i < steps.length - 1 && <span className="flex-1 h-px bg-line" />}
          </React.Fragment>
        ))}
      </div>

      <Card>
        {/* Step 0 — name (+ context block for context mode) */}
        {step === 0 && (
          <div>
            <MonoLabel className="mb-2">Name your skill</MonoLabel>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-line px-3 py-2 text-[14px] font-medium mb-4 focus:outline-none focus:ring-2 focus:ring-brand-200" />
            {starterName && (
              <p className="text-[12px] text-brand-600 -mt-2 mb-3 flex items-center gap-1">
                <Wand2 size={12} /> Starting from “{starterName}” — adjust it, don't compose it.
              </p>
            )}
            {suggestions.length > 0 && (
              <div className="rounded-lg border border-brand-100 bg-brand-50/50 px-3.5 py-3 mb-4">
                <p className="text-[12px] font-semibold text-ink mb-2">
                  Something like this already exists — start from it instead of a blank box:
                </p>
                <div className="space-y-1.5">
                  {suggestions.slice(0, 3).map((s) => (
                    <button key={s.id} type="button" onClick={() => adoptSuggestion(s)}
                      className="w-full text-left rounded-md bg-white border border-line hover:border-brand-300 px-3 py-2">
                      <span className="text-[12.5px] font-semibold text-ink">{s.name}</span>
                      <span className="text-[11.5px] text-ink-dim"> · {(s.process_steps || []).length} steps{s.problem ? ` — ${s.problem.slice(0, 80)}` : ''}</span>
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => { setSuggestions([]); setSuggestionsDismissed(true); }}
                  className="text-[11.5px] text-ink-soft hover:text-ink-muted mt-2">No thanks — start fresh</button>
              </div>
            )}
            {mode === 'context' ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <MonoLabel>Here's what we know about you</MonoLabel>
                  <DictateButton onText={(t) => setAbout((v) => (v ? `${v}\n${t}` : t))} />
                </div>
                <p className="text-[12px] text-ink-dim mb-2">Drafted from your onboarding — edit anything that's off.</p>
                <textarea value={about} onChange={(e) => setAbout(e.target.value)} rows={9}
                  className="w-full rounded-lg border border-line px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-200" />
              </>
            ) : (
              <>
                <p className="text-[12.5px] text-ink-dim">{prefill?.problem}</p>
                {/* TD-3: their tool-dive answers carry forward — nobody is asked twice. */}
                {(prefill?.profile_decided ?? 0) > 0 && (
                  <p className="text-[11.5px] text-brand-600 mt-2">
                    Your delegation profile applies ({prefill!.profile_decided} decisions from your tool dives)
                    {prefill?.act_policy === 'no' && ' — the assistant never sends; you do.'}
                    {prefill?.act_policy === 'approval' && ' — nothing is sent without your approval.'}
                    {(prefill?.act_policy === 'yes' || !prefill?.act_policy) && '.'}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 1 — context: extras · workflow: the steps */}
        {step === 1 && mode === 'context' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <MonoLabel>Anything AI should always know?</MonoLabel>
              <DictateButton onText={(t) => setExtras((v) => (v ? `${v}\n${t}` : t))} />
            </div>
            <p className="text-[12px] text-ink-dim mb-2">Optional. Constraints, audience, recurring context.</p>
            <textarea value={extras} onChange={(e) => setExtras(e.target.value)} rows={6} autoFocus
              placeholder="e.g. My audience is non-technical customers. Never share internal pricing."
              className="w-full rounded-lg border border-line px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-200" />
          </div>
        )}
        {step === 1 && mode === 'workflow' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <MonoLabel>The steps</MonoLabel>
              <DictateButton onText={(t) => setStepsText((v) => (v ? `${v}\n${t}` : t))} />
            </div>
            <p className="text-[12px] text-ink-dim mb-2">One step per line. We've drafted a skeleton — fill in the specifics for your task.</p>
            <textarea value={stepsText} onChange={(e) => setStepsText(e.target.value)} rows={8} autoFocus
              className="w-full rounded-lg border border-line px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-200" />
          </div>
        )}

        {/* Step 2 — context: style · workflow: an example */}
        {step === 2 && mode === 'context' && (
          <div>
            <MonoLabel className="mb-2">Your style</MonoLabel>
            <p className="text-[12px] text-ink-dim mb-2">Optional. Tone and format you prefer.</p>
            <textarea value={style} onChange={(e) => setStyle(e.target.value)} rows={6} autoFocus
              placeholder="e.g. Warm but concise. Bullet points over paragraphs. UK English."
              className="w-full rounded-lg border border-line px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-200" />
          </div>
        )}
        {step === 2 && mode === 'workflow' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <MonoLabel>An example (optional)</MonoLabel>
              <div className="flex items-center gap-3">
                <AttachExamples onText={(t) => setExampleIn((v) => (v ? `${v}\n\n${t}` : t))} />
                <DictateButton onText={(t) => setExampleIn((v) => (v ? `${v}\n${t}` : t))} />
              </div>
            </div>
            <p className="text-[12px] text-ink-dim mb-2">A real input you'd give, and what a good result looks like — attach the files you used plus the finished thing, the inputs explain the output.</p>
            <textarea value={exampleIn} onChange={(e) => setExampleIn(e.target.value)} rows={4} autoFocus
              placeholder="Example input…"
              className="w-full rounded-lg border border-line px-3 py-2 text-[13px] leading-relaxed mb-2 focus:outline-none focus:ring-2 focus:ring-brand-200" />
            <textarea value={exampleOut} onChange={(e) => setExampleOut(e.target.value)} rows={4}
              placeholder="What good output looks like…"
              className="w-full rounded-lg border border-line px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-200" />
          </div>
        )}

        {/* Step 3 — preview */}
        {step === 3 && prefill && (
          <div>
            <MonoLabel className="mb-2">Preview — your reusable skill</MonoLabel>
            <div className="text-[14px] font-semibold text-ink">{name.trim() || prefill.name}</div>
            <p className="text-[12.5px] text-ink-dim mb-3">{prefill.problem}</p>
            {mode === 'context' ? (
              <div className="rounded-lg border border-line bg-canvas px-3 py-2.5 whitespace-pre-wrap text-[13px] text-ink leading-relaxed">
                {assembleContext(about, extras, style) || 'Your context block will appear here.'}
              </div>
            ) : (
              <div>
                <ol className="list-decimal pl-5 space-y-1 text-[13px] text-ink">
                  {stepsText.split('\n').map((s) => s.trim()).filter(Boolean).map((s, i) => <li key={i}>{s}</li>)}
                </ol>
                {(exampleIn || exampleOut) && (
                  <div className="mt-3 rounded-lg border border-line bg-canvas px-3 py-2.5 text-[12.5px] text-ink-muted whitespace-pre-wrap">
                    {exampleIn && <div><span className="font-semibold">Example in:</span> {exampleIn}</div>}
                    {exampleOut && <div className="mt-1"><span className="font-semibold">Good output:</span> {exampleOut}</div>}
                  </div>
                )}
                <p className="text-[11.5px] text-ink-soft mt-3">After saving, run it on real work and log how it went — that's the vetting loop.</p>
              </div>
            )}
          </div>
        )}

        {error && <div className="mt-3"><ErrorNote message={error} /></div>}

        <div className="flex items-center justify-between mt-5">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || saving}
            className="inline-flex items-center gap-1.5 text-[13px] text-ink-dim hover:text-ink disabled:opacity-30">
            <ArrowLeft size={15} /> Back
          </button>
          {step < steps.length - 1 ? (
            <button onClick={() => setStep((s) => s + 1)} disabled={!stepValid}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
              Next <ArrowRight size={15} />
            </button>
          ) : (
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
              {saving && <Loader2 size={14} className="animate-spin" />} {reviseFrom ? 'Save as new skill' : 'Save my skill'}
            </button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SkillBuilderPage;
